import { useEffect, useState } from 'react'
import SiteLink from '@/components/SiteLink'
import { LINKS } from './shopData'
import { ShopButton, Reveal } from './ShopUI'

// The three products featured on the homepage, loaded from the same catalog
// the /store pages use (live Storefront API with the mock as fallback) so
// titles, prices, and photos always match the store. The store lib is
// dynamically imported to keep it out of the eager landing-page bundle.
const FEATURED_HANDLES = ['athletic-tee-black', 'dub-apex-hooded-quarter-zip', 'casual-tee-black']

export default function ShopMerch() {
  const [products, setProducts] = useState(null)

  useEffect(() => {
    let active = true
    import('@/store/lib/shopify')
      .then((m) => m.getProducts())
      .then((all) => {
        if (!active) return
        const byHandle = new Map(all.map((p) => [p.handle, p]))
        setProducts(FEATURED_HANDLES.map((h) => byHandle.get(h)).filter(Boolean))
      })
      .catch(() => { if (active) setProducts([]) })
    return () => { active = false }
  }, [])

  return (
    <section className="relative overflow-hidden bg-white py-20 sm:py-24">
      {/* Soft washes: lemon bottom-left, lavender right — like the original. */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -bottom-24 -left-24 h-96 w-96 rounded-full bg-[#EBFF59]/45 blur-3xl" />
        <div className="absolute inset-y-0 right-0 w-1/3 bg-[radial-gradient(70%_90%_at_100%_60%,rgba(182,139,255,0.4)_0%,rgba(182,139,255,0)_70%)]" />
      </div>

      <div className="relative mx-auto max-w-6xl px-5 sm:px-8">
        <Reveal>
          {/* Type set inline: the design system's unlayered h2 rule beats
              Tailwind's layered text-* utilities. */}
          <h2
            className="max-w-xl font-[var(--font-heading)] font-extrabold text-[#292237]"
            style={{ fontSize: 'clamp(2.25rem, 3.5vw, 3rem)', lineHeight: 1.1, letterSpacing: '-0.02em' }}
          >
            Win more than just bragging rights
          </h2>
          <p className="mt-5 text-[#66626E]">Top dubs get free merch!</p>
        </Reveal>

        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {!products &&
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] animate-pulse rounded-[1.5rem] bg-[#292237]/5" />
            ))}
          {products?.map((p, i) => (
            <Reveal key={p.handle} delay={i * 100}>
              <div className="flex h-full flex-col rounded-[1.5rem] bg-white p-6 shadow-[0_25px_50px_-25px_rgba(41,34,55,0.35)] ring-1 ring-[#292237]/6 transition-transform duration-300 hover:-translate-y-1">
                <SiteLink to={LINKS.product(p.handle)} className="block overflow-hidden">
                  <img
                    src={p.images?.[0]?.url}
                    alt={p.title}
                    className="aspect-square w-full object-contain transition-transform duration-500 hover:scale-105"
                    loading="lazy"
                  />
                </SiteLink>
                <SiteLink
                  to={LINKS.product(p.handle)}
                  className="mt-4 font-[var(--font-heading)] text-lg font-bold text-[#292237] hover:text-[#460069]"
                >
                  {p.title}
                </SiteLink>
                <div className="mt-2 text-[#66626E]">
                  ${Number(p.price).toFixed(2)}
                </div>
                <div className="mt-5 flex-1" />
                <ShopButton
                  variant="secondary"
                  href={LINKS.product(p.handle)}
                  className="w-full !py-2.5 text-[15px]"
                >
                  Add to cart
                </ShopButton>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-12 text-center">
          <ShopButton variant="secondary" href={LINKS.store}>
            Shop all products
          </ShopButton>
        </Reveal>
      </div>
    </section>
  )
}
